import { ref, computed } from 'vue'

export type ValidatorFn = (value: string, ...args: any[]) => string | undefined
export type AsyncValidatorFn = (value: string) => Promise<string>

export interface ValidationField {
  value: string
  validators?: ValidatorFn[]
  asyncValidators?: AsyncValidatorFn[]
}

export type ValidationSchema = Record<string, ValidationField>

export function useFormValidation() {
  const errors = ref<Record<string, string>>({})

  const isValid = computed(() =>
    Object.values(errors.value).every(e => !e),
  )

  function validate(
    field: string,
    value: string,
    validators: ValidatorFn[],
  ): boolean {
    for (const validator of validators) {
      const result = validator(value)
      if (result) {
        errors.value[field] = result
        return false
      }
    }
    errors.value[field] = ''
    return true
  }

  async function validateAsync(
    field: string,
    value: string,
    asyncValidators: AsyncValidatorFn[],
  ): Promise<boolean> {
    for (const validator of asyncValidators) {
      const result = await validator(value)
      if (result) {
        errors.value[field] = result
        return false
      }
    }
    errors.value[field] = ''
    return true
  }

  function validateAll(schema: ValidationSchema): boolean {
    let allValid = true
    for (const [field, definition] of Object.entries(schema)) {
      if (definition.validators && definition.validators.length > 0) {
        const fieldValid = validate(field, definition.value, definition.validators)
        if (!fieldValid) allValid = false
      }
    }
    return allValid
  }

  async function validateAllAsync(schema: ValidationSchema): Promise<boolean> {
    let allValid = validateAll(schema)
    for (const [field, definition] of Object.entries(schema)) {
      if (definition.asyncValidators && definition.asyncValidators.length > 0) {
        const fieldValid = await validateAsync(field, definition.value, definition.asyncValidators)
        if (!fieldValid) allValid = false
      }
    }
    return allValid
  }

  function clearErrors(): void {
    errors.value = {}
  }

  function clearError(field: string): void {
    errors.value[field] = ''
  }

  function setError(field: string, message: string): void {
    errors.value[field] = message
  }

  return {
    errors,
    isValid,
    validate,
    validateAsync,
    validateAll,
    validateAllAsync,
    clearErrors,
    clearError,
    setError,
  }
}
